/**
 * 使用者偏好設定
 * -----------------------------------------------------------------------------
 * **R48 D3 redesign 砍 theme + density**(對齊 D3 啟動包「無暗模式 / 桌面優先」拍板):
 * - 砍 `theme: 'moon' | 'sun'`(月亮 / 太陽 色系切換)
 * - 砍 `density: 'standard' | 'large'`(標準 / 放大 字級切換)
 * - theme.css `[data-theme='sun']` + `[data-density='large']` 兩個 selector 區塊一併砍
 * - useThemePreference hook + Provider + test 全套砍
 * - ThemeMenu 內 theme picker + density toggle UI 砍
 *
 * 只留 `locale`(語系切換,未來支援 en 用)。schema 保留是為了:
 * - normalize 仍能讀舊 localStorage 的 `theme: 'moon'|'sun'` / `density: 'large'` 等
 *   stale data graceful drop(不擾用戶體驗)
 * - 未來若要加新 prefs(eg. `defaultBrandKitId`)有 store 殼可用
 *
 * @remarks
 * 既有 callers 用 `services.prefsStore.load() / save({ theme, density, locale })` 的 patch
 * shape 仍 backward compat:額外 field 進 normalize 自動 drop(只保留 locale)。
 */

export type LocaleCode = 'zh-TW' | 'en' | 'ja';

/**
 * M-04 AssetLibraryPage 三檢視模式(R30+ 視覺重做):
 * - `list`(預設)— 表格式 12 欄列檢視(thumbnail / name / type / spec / source / project /
 *   status / ABCD / date / actions)
 * - `grid` — R30 既有網格卡片(每卡 220px,thumb 4:3)
 *
 * (`adPack` 2026-08-10 移除 —— 投放包檢視整組刪除。**存量偏好會留著舊值**,
 * 所以下面的 normalize 不認得它就會回 undefined → 落回預設 `list`,不會白畫面。)
 */
export type AssetLibraryViewMode = 'list' | 'grid';

export interface Preferences {
  locale: LocaleCode;
  /**
   * AssetLibraryPage 檢視模式偏好(M-04)。未設時走 default 'list'。
   * caller 用 `services.prefsStore.save({ assetLibraryViewMode: 'grid' })` 持久化。
   */
  assetLibraryViewMode?: AssetLibraryViewMode;
}

/**
 * PrefsStoreService 介面
 * by-design client-side(localStorage);未來真後端:`GET/PUT /me/preferences`,
 * 換個 `createPrefsStore` impl 即可,caller 不必動。
 *
 * R41.6 起 implementation 收進本檔(砍 mock layer)。
 */
export interface PrefsStoreService {
  load(): Promise<Preferences>;
  save(patch: Partial<Preferences> & Record<string, unknown>): Promise<void>;
}

const PREFS_KEY = 'hai.prefs';

const DEFAULT_PREFS: Preferences = {
  locale: 'zh-TW',
};

/**
 * localStorage-backed PrefsStore。JSON parse 失敗時回退預設值,避免髒資料炸 UI。
 *
 * R48 D3 redesign:`normalize` 砍 theme + density 處理,只 normalize `locale`;舊
 * localStorage 內 stale `theme` / `density` field 直接 graceful drop。
 */
export function createPrefsStore(): PrefsStoreService {
  async function load(): Promise<Preferences> {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      return normalize(parsed);
    } catch {
      return { ...DEFAULT_PREFS };
    }
  }

  return {
    load,
    async save(patch) {
      const current = await load();
      // 砍 stale field(theme / density / elderMode 等);只保留 Preferences 認識的
      const next = normalize({ ...current, ...patch });
      localStorage.setItem(PREFS_KEY, JSON.stringify(next));
    },
  };
}

function normalizeLocale(value: unknown): Preferences['locale'] {
  if (value === 'en' || value === 'ja') return value;
  return 'zh-TW';
}

function normalizeAssetLibraryViewMode(
  value: unknown,
): AssetLibraryViewMode | undefined {
  // 舊值 `'adPack'` 刻意不認(檢視已刪)→ undefined → caller 落回預設 'list'
  if (value === 'list' || value === 'grid') return value;
  return undefined;
}

function normalize(parsed: Record<string, unknown>): Preferences {
  const viewMode = normalizeAssetLibraryViewMode(parsed.assetLibraryViewMode);
  const out: Preferences = {
    locale: normalizeLocale(parsed.locale),
  };
  if (viewMode) out.assetLibraryViewMode = viewMode;
  return out;
}
